import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useRealPathName } from './sitecore/useRealPathName';
import type { Favorite } from 'lib/services/favorites-service';
import { useUserProfileUrls } from './useUserProfileUrls';
import { useRouterUtils } from 'lib/utils/router-utils';
import { useSessionSafe } from './auth/useSessionSafe';

export interface UseFavoriteOptions {
  contentId: string;
  title?: string;
  contentType?: string;
  siteId?: string;
  onError?: (error: Error | unknown) => void;
}

export interface UseFavoriteReturn {
  isFavorite: boolean;
  isProcessing: boolean;
  status: string;
  toggleFavorite: () => Promise<void>;
  hasProcessedAddFavorite: boolean;
}

/**
 * Custom hook for managing favorite functionality
 * Handles checking favorite status, adding/removing favorites, session updates, and authentication redirects
 *
 * @param options - Configuration options for the hook
 * @returns Object containing isFavorite status, isProcessing state, and toggleFavorite function
 */
export function useFavorite(options: UseFavoriteOptions): UseFavoriteReturn {
  const { contentId, title, contentType, siteId, onError } = options;
  const { data: session, status, update } = useSessionSafe();
  const [isProcessing, setIsProcessing] = useState(false);
  const pathname = useRealPathName();
  const router = useRouter();
  const { loginUrl } = useUserProfileUrls();
  const optimisticFavoriteRef = useRef<boolean | null>(null);
  // Check if content is already favorited
  const isFavorite = useMemo(() => {
    if (optimisticFavoriteRef.current !== null) {
      return optimisticFavoriteRef.current;
    }
    if (!contentId || !session?.user?.favorites) {
      return false;
    }
    return session.user.favorites.some((favorite) => favorite.Content.Id === contentId);
  }, [contentId, session?.user?.favorites]);

  const toggleFavorite = useCallback(async () => {
    if (isProcessing || !contentId) {
      return;
    }

    // If session is refreshing/loading, do nothing (prevents profile flash)
    if (status === 'loading') {
      return;
    }
    optimisticFavoriteRef.current = !isFavorite;
    // If not authenticated, redirect to login
    if (status !== 'authenticated' || !session?.user?.email) {
      const returnQuery = new URLSearchParams();
      Object.entries(router.query).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => returnQuery.append(key, v));
        } else if (value !== undefined) {
          returnQuery.set(key, value);
        }
      });
      returnQuery.set('AddFavorite', contentId);
      const returnUrl = `${pathname}?${returnQuery.toString()}`;
      router.push({
        pathname: loginUrl,
        query: {
          regAction: 'FAVORITE',
          esrc: contentId,
          sRegistrationFirstStep: 'false',
          returnUrl: returnUrl,
        },
      });
      return;
    }

    const accessToken = session?.user?.accessToken ?? '';

    if (!accessToken) {
      const error = new Error('No access token available');
      console.error(error.message);
      onError?.(error);
      return;
    }

    setIsProcessing(true);

    try {
      if (!isFavorite) {
        // Add favorite via API
        const response = await fetch('/api/favorites', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            Content: {
              Id: contentId,
              Title: title ?? '',
              ContentType: contentType ?? '',
            },
          }),
        });

        if (response.ok) {
          const data = await response.json();

          // Update session with new favorite
          const newFavorite: Favorite = {
            Id: data.Id || `fav-${Date.now()}`,
            Content: {
              Id: contentId,
              Title: title ?? '',
              ContentType: contentType ?? '',
            },
            SiteId: siteId ?? '',
          };

          const updatedFavorites = [...(session?.user?.favorites ?? []), newFavorite];
          await update({ user: { favorites: updatedFavorites } });
        } else {
          const errorData = await response.json().catch(() => ({}));
          const error = new Error(errorData.error_description || 'Failed to add favorite');
          console.error('Failed to add favorite:', errorData);
          onError?.(error);
        }
      } else {
        // Delete favorite via API
        const response = await fetch(`/api/favorites?contentId=${encodeURIComponent(contentId)}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.ok) {
          // Update session by removing the favorite
          const updatedFavorites =
            session?.user?.favorites?.filter((favorite) => favorite.Content.Id !== contentId) ?? [];
          await update({ user: { favorites: updatedFavorites } });
        } else {
          const errorData = await response.json().catch(() => ({}));
          const error = new Error(errorData.error_description || 'Failed to delete favorite');
          console.error('Failed to delete favorite:', errorData);
          onError?.(error);
        }
      }
    } catch (error) {
      console.error('Error processing favorite:', error);
      onError?.(error);
    } finally {
      optimisticFavoriteRef.current = null;
      setIsProcessing(false);
    }
  }, [
    contentId,
    title,
    contentType,
    siteId,
    isFavorite,
    isProcessing,
    status,
    session,
    pathname,
    router,
    update,
    loginUrl,
    onError,
  ]);

  // Track if we've already processed the AddFavorite query parameter
  const hasProcessedAddFavorite = useRef(false);

  // Reset the ref when contentId changes so different content items can be processed
  useEffect(() => {
    hasProcessedAddFavorite.current = false;
  }, [contentId]);

  const { removeQueryParameter, isReady } = useRouterUtils();
  // Check for AddFavorite=true in query string and automatically add favorite
  useEffect(() => {
    if (!isReady) {
      return;
    }
    const addFavoriteParam = router.query.AddFavorite;
    const shouldAddFavorite =
      addFavoriteParam === contentId ||
      (Array.isArray(addFavoriteParam) && addFavoriteParam[0] === contentId);

    if (
      shouldAddFavorite &&
      !hasProcessedAddFavorite.current &&
      !isProcessing &&
      contentId &&
      status === 'authenticated' &&
      session?.user?.email &&
      !isFavorite
    ) {
      hasProcessedAddFavorite.current = true;

      // Remove AddFavorite from query string
      removeQueryParameter('AddFavorite');

      // Automatically add favorite
      toggleFavorite().catch((error) => {
        console.error('Error auto-adding favorite from query string:', error);
        onError?.(error);
      });
    }
  }, [
    contentId,
    isFavorite,
    isProcessing,
    onError,
    removeQueryParameter,
    router.query.AddFavorite,
    session?.user?.email,
    status,
    toggleFavorite,
    isReady,
  ]);

  return {
    isFavorite,
    isProcessing,
    status,
    toggleFavorite,
    hasProcessedAddFavorite: hasProcessedAddFavorite.current,
  };
}
